import { api } from "@/utils/axiosInterceptor";
import { Input } from "./ui/input";
import { useQuery } from "@tanstack/react-query";
import { ChangeEvent, useCallback, useState } from "react";
import { Button } from "./ui/button";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { EInvoice } from "@/types/e-invoice";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  Row,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import DataTable from "./ui/data-table";
import LoadingIndicator from "./LoadingIndicator";
import { Checkbox } from "./ui/checkbox";
import { endOfDay, format, startOfDay } from "date-fns";
import { Label } from "./ui/label";
import { DateFilterValue } from "./modal-invoice";

type SearchInvoiceReferenceProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (EInvoices: EInvoice[]) => void;
  buyerTin: string;
  defaultChecked?: string[];
};

const columns: ColumnDef<EInvoice>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
  },
  {
    accessorKey: "id",
    header: "UUID",
  },
  {
    accessorKey: "internalId",
    header: "Invoice Number",
  },
  {
    accessorKey: "posReference",
    header: "POS Ref Number",
  },
  {
    accessorKey: "issuer",
    header: "Supplier",
  },
  {
    accessorKey: "receiver",
    header: "Buyer",
  },
  {
    accessorKey: "totalPayableAmount",
    header: "Total (RM)",
  },
  {
    accessorKey: "eInvoiceDate",
    header: "Date ",
    cell: ({ row }) => {
      const date = row.original.eInvoiceDate;

      return format(date, "dd/MM/yyyy");
    },
    filterFn: (
      row: Row<EInvoice>,
      columnId: string,
      filterValue: { fromDate: string; toDate: string }
    ) => {
      const invoiceDate = row.getValue(columnId) as Date;
      const { fromDate, toDate } = filterValue;

      let matchesDate = true;
      if (fromDate || toDate) {
        // Normalize invoiceDate to start of day for comparison
        const invoiceDateStart = startOfDay(invoiceDate);
        const fromDateObj = fromDate ? startOfDay(new Date(fromDate)) : null;
        const toDateObj = toDate ? endOfDay(new Date(toDate)) : null;

        if (fromDateObj && invoiceDateStart < fromDateObj) {
          matchesDate = false;
        }
        if (toDateObj && invoiceDateStart > toDateObj) {
          matchesDate = false;
        }
      }
      return matchesDate;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <span className="px-3 py-0.5 rounded-full bg-green-500 text-green-200">
        {row.original.status}
      </span>
    ),
  },
];

const SearchInvoiceReferenceNumber = ({
  isOpen,
  onClose,
  onSelect,
  buyerTin,
  defaultChecked,
}: SearchInvoiceReferenceProps) => {
  const [selectedRow, setSelectedRow] = useState<EInvoice[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [tableInstance, setTableInstance] = useState<ReturnType<
    typeof useReactTable<EInvoice>
  > | null>(null);

  const { data: eInvoices, isLoading } = useQuery({
    queryKey: ["invoiceoriginalInvoiceReference", globalFilter],
    queryFn: async () => {
      const response = await api.get<EInvoice[]>(
        `/e-invoices?buyerTin=${buyerTin}&status=Valid`
      );

      let sortedData = response.data;
      if (defaultChecked && defaultChecked.length > 0) {
        const preSelected: EInvoice[] = [];
        const notSelected: EInvoice[] = [];

        response.data.forEach((row) => {
          const isPreSelected =
            defaultChecked.includes(row.invoiceId) ||
            defaultChecked.includes(row.internalId);
          if (isPreSelected) {
            preSelected.push(row);
          } else {
            notSelected.push(row);
          }
        });

        sortedData = [...preSelected, ...notSelected];

        const selection: RowSelectionState = {};
        response.data.forEach((row) => {
          const isPreSelected =
            defaultChecked.includes(row.invoiceId) ||
            defaultChecked.includes(row.internalId);
          if (isPreSelected) {
            selection[String(row.id)] = true;
          }
        });
        setRowSelection(selection);
      }

      return sortedData;
    },
    enabled: isOpen,
  });

  const globalFilterFn: FilterFn<EInvoice> = (
    row: Row<EInvoice>,
    _columnId: string,
    filterValue: string
  ) => {
    const searchValue = filterValue.toLowerCase();
    const internalId = String(row.original.internalId || "").toLowerCase();
    const posReference = String(row.original.posReference || "").toLowerCase();
    return (
      !searchValue ||
      internalId.includes(searchValue) ||
      posReference.includes(searchValue)
    );
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
  };

  const handleRowSelection = (selectedRow: EInvoice[]) => {
    setSelectedRow(selectedRow);
  };

  const handleSelectAllRow = useCallback(() => {
    if (tableInstance) {
      tableInstance.toggleAllRowsSelected(true); //select all rows across all pages
    }
  }, [tableInstance]);

  const deselectAllRows = useCallback(() => {
    if (tableInstance) {
      tableInstance.toggleAllRowsSelected(false); //deselect all rows across all pages
    }
  }, [tableInstance]);

  const handleFromDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fromDate = e.target.value;
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === "eInvoiceDate");
      const existingValue: DateFilterValue = existing
        ? (existing.value as DateFilterValue)
        : { fromDate: "", toDate: "" };
      return [
        ...prev.filter((filter) => filter.id !== "eInvoiceDate"),
        { id: "eInvoiceDate", value: { ...existingValue, fromDate } },
      ];
    });
  };

  const handleToDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const toDate = e.target.value;
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === "eInvoiceDate");
      const existingValue: DateFilterValue = existing
        ? (existing.value as DateFilterValue)
        : { fromDate: "", toDate: "" };
      return [
        ...prev.filter((filter) => filter.id !== "eInvoiceDate"),
        { id: "eInvoiceDate", value: { ...existingValue, toDate } },
      ];
    });
  };

  //get the filter value for the inputs
  const invoiceDateFilter = columnFilters.find((f) => f.id === "eInvoiceDate");
  const dateFilterValue = (invoiceDateFilter?.value as DateFilterValue) || {
    fromDate: "",
    toDate: "",
  };

  if (!isOpen) return null;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        onInteractOutside={(event) => event.preventDefault()}
        className="max-w-7xl h-fit"
      >
        <DialogHeader>
          <DialogTitle>E-Invoices</DialogTitle>
          <DialogDescription>List of valid E-Invoices</DialogDescription>
        </DialogHeader>
        <div className="relative flex flex-col gap-2.5">
          <div className="w-full">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Invoice number/POS reference number"
              value={globalFilter}
              onChange={handleSearch}
              className="pl-8 max-w-[50%]"
              aria-label="Search invoices"
            />
          </div>
          <div className="w-full flex justify-between items-center">
            <div className="w-fit flex items-center gap-2.5 text-gray-500">
              <Label>From</Label>
              <Input
                type="date"
                value={dateFilterValue.fromDate}
                onChange={handleFromDateChange}
                aria-label="From date"
              />
              <Label>To</Label>
              <Input
                type="date"
                value={dateFilterValue.toDate}
                onChange={handleToDateChange}
                aria-label="To date"
              />
              <Button size="sm" variant="ghost" onClick={handleSelectAllRow}>
                Select All Pages
              </Button>
              <Button size="sm" variant="ghost" onClick={deselectAllRows}>
                Deselect All
              </Button>
            </div>
            <Button
              size="sm"
              onClick={() => {
                onSelect(selectedRow);
                onClose();
              }}
            >
              Select E-Invoices
            </Button>
          </div>
        </div>
        {isLoading ? (
          <div className="min-h-[30rem]">
            <LoadingIndicator />
          </div>
        ) : (
          <DataTable<EInvoice>
            data={eInvoices!}
            columns={columns}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            onRowSelect={handleRowSelection}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            globalFilterFn={globalFilterFn}
            onTableInstance={setTableInstance}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchInvoiceReferenceNumber;
